import React, { useEffect, useState } from "react";

import * as yup from "yup";
import { Autocomplete, Box, Button, Modal, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useActionHook, useCanCreateDeal } from "hooks";
import { IApiServiceError, useDealActions, useReportsActions, useTaskDealsSelect } from "store";
import {
  ApiValidationErrors,
  CurrencyType,
  IAssetClass,
  ICommitment,
  IInvestorBankDetails,
  IQuickViewCreateNewItem,
  IRound
} from "store/types";

import { CreateModal } from "../modals/CreateModal";

interface IAddNewLineProps {
  onClose: () => void;
  refetchTable: () => void;
}

export interface IValues {
  deal: IDealsOptions | null;
  round: IRoundOptions | null;
  investor: IInvestorsOptions | null;
}
export interface IDealsOptions {
  id?: number;
  title: string;
  asset_class?: IAssetClass;
  currency?: CurrencyType;
}

export interface IRoundOptions {
  id?: number;
  account_id?: number;
  deal_id?: number;
  name: string;
  deal_commitments?: ICommitment[];
  created_at?: string;
  updated_at?: string;
  date?: string;
}
export interface IInvestorsOptions {
  id?: number;
  account_id?: number;
  name: string;
  email?: string;
  phone_number?: string;
  company?: string;
  additional_information?: string;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
  bank_details?: IInvestorBankDetails | null;
}

export const AddNewLine: React.FC<IAddNewLineProps> = ({ onClose, refetchTable }) => {
  const deals = useTaskDealsSelect();
  const { createQuickViewLineAction, getQuickViewMetadata } = useReportsActions();
  const { getRoundsAction, getAvailableInvestorsList } = useDealActions();
  const [createDealModal, setCreateDealModal] = useState<"deal" | "investor" | "round" | null>(null);
  const canCreateDeal = useCanCreateDeal();
  const [error, setError] = useState<ApiValidationErrors | null>(null);

  const { action: createNewLine } = useActionHook(createQuickViewLineAction, {
    onSuccess: async () => {
      resetForm();
      onClose();
      await getQuickViewMetadata();
      refetchTable();
    },
    onReject: (error) => {
      setError((error as IApiServiceError).errors);
    }
  });

  const [dealsOptions, setDealsOptions] = useState<IDealsOptions[]>([]);
  const [roundsOptions, setRoundsOptions] = useState<IRoundOptions[]>([]);
  const [investorsOptions, setInvestorsOptions] = useState<IInvestorsOptions[]>([]);

  const initialValues: IValues = {
    deal: null,
    investor: null,
    round: null
  };

  const { values, setFieldValue, isValid, resetForm, handleSubmit } = useFormik({
    validateOnMount: true,
    initialValues: { ...initialValues },
    validationSchema: yup.object({
      deal: yup.object({
        title: yup.string().required()
      }),
      investor: yup.object({
        name: yup.string().required()
      }),
      round: yup.object({
        name: yup.string().required()
      })
    }),
    onSubmit: async () => {
      const data: IQuickViewCreateNewItem = {
        deal: values?.deal?.id
          ? { id: values.deal.id }
          : {
              title: values?.deal?.title,
              asset_class_id: Number(values?.deal?.asset_class?.id),
              currency: values?.deal?.currency
            },
        round: values?.round?.id ? { id: Number(values.round.id) } : { name: values?.round?.name },
        investor: values?.investor?.id ? { id: Number(values.investor.id) } : { ...values?.investor }
      };
      await createNewLine(data);
    }
  });

  const saveInvestorsToOptions = async (dealId: number | undefined, roundId: number | undefined) => {
    const investorsList = await getAvailableInvestorsList({ deal_id: dealId, round_id: roundId });
    setInvestorsOptions(investorsList);
  };

  useEffect(() => {
    saveInvestorsToOptions(values?.deal?.id, values?.round?.id);
  }, [values?.deal?.id, values?.round?.id]);

  const getRounds = async (id: number) => {
    if (!values.deal || !values.deal.id) return;
    const roundsList = await getRoundsAction({ dealId: id });
    setRoundsOptions([...(roundsList as IRound[])]);
  };

  useEffect(() => {
    if (!deals) return;
    setDealsOptions(deals);
  }, [deals]);

  useEffect(() => {
    if (!values?.deal || !values?.deal?.id) {
      setRoundsOptions([]);
      return;
    }
    setRoundsOptions([]);
    getRounds(values?.deal?.id);
  }, [values.deal?.id]);

  useEffect(() => {
    if (!values?.deal?.id && !!values.deal) {
      setFieldValue("round.name", "Single round");
      setRoundsOptions([{ name: "Single round" }]);
    }
  }, [dealsOptions]);

  useEffect(() => {
    if (roundsOptions?.length === 1) {
      setFieldValue("round", roundsOptions[0]);
    }
  }, [roundsOptions]);

  const onDealChangeHandler = (value: IDealsOptions | null) => {
    resetForm();
    setRoundsOptions([]);
    setFieldValue("deal", value);
    setFieldValue("round", null);
    setFieldValue("investor", null);
  };

  return (
    <Box
      component="div"
      sx={{
        marginBottom: "10px",
        position: "relative",
        width: "100%",
        height: "69px",
        borderRadius: "10px",
        border: "solid 1px #dae0e2",
        backgroundColor: "#fff",
        padding: "8px 15px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}
    >
      <Box sx={{ width: "100%", display: "flex", gap: "10px" }}>
        <Autocomplete
          clearOnBlur
          value={values.deal}
          sx={{
            width: "306px",
            position: "relative",
            "& .MuiPaper-root::-webkit-scrollbar": {
              width: "20px"
            }
          }}
          onChange={(e, value) => {
            setError(null);
            onDealChangeHandler(value);
          }}
          PaperComponent={({ children }) => {
            return (
              <Paper
                sx={{
                  position: "absolute",
                  maxWidth: "306px",
                  backgroundColor: "white",
                  zIndex: "10",
                  borderRadius: "10px",
                  "& .MuiAutocomplete-option:hover": {
                    backgroundColor: "#edf1f9"
                  }
                }}
              >
                <>
                  {children}
                  {canCreateDeal && (
                    <Button
                      onMouseDown={() => {
                        setCreateDealModal("deal");
                      }}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Add Deal
                    </Button>
                  )}
                </>
              </Paper>
            );
          }}
          getOptionLabel={(value) => {
            return value.title;
          }}
          options={dealsOptions || []}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IDealsOptions) => (
            <li style={{ height: "43px" }} {...props} key={option.id + option.title}>
              <p
                style={{
                  width: "293px",
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  color: "#0a3142"
                }}
              >
                {option.title}
              </p>
            </li>
          )}
          renderInput={(params) => {
            return (
              <TextField
                sx={{
                  maxWidth: "306px",
                  ".MuiFormHelperText-root": {
                    top: "38px",
                    position: "absolute",
                    margin: 0
                  }
                }}
                {...params}
                required
                label="Deal"
                error={!!error?.["deal.title"]}
                helperText={error?.["deal.title"]?.[0]}
              />
            );
          }}
        />
        <Autocomplete
          sx={{ width: "306px" }}
          disabled={!values?.deal?.title}
          clearOnBlur={true}
          value={values?.round}
          options={roundsOptions || []}
          onChange={(e, value) => {
            setFieldValue("round", value);
          }}
          getOptionLabel={(value) => {
            return value.name;
          }}
          PaperComponent={({ children }) => {
            return (
              <Paper
                sx={{
                  position: "absolute",
                  maxWidth: "306px",
                  backgroundColor: "white",
                  zIndex: "10",
                  borderRadius: "10px",
                  "& .MuiAutocomplete-option:hover": {
                    backgroundColor: "#edf1f9"
                  }
                }}
              >
                <>
                  {children}
                  <Button
                    onMouseDown={() => {
                      setCreateDealModal("round");
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      height: "50px",
                      backgroundColor: "#fff",
                      boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                      borderRadius: "0 0 10px 10px"
                    }}
                    fullWidth
                  >
                    Add Round
                  </Button>
                </>
              </Paper>
            );
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IRoundOptions) => (
            <li style={{ height: "43px" }} {...props} key={option.id + option.name}>
              <p
                style={{
                  width: "293px",
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  color: "#0a3142"
                }}
              >
                {option.name}
              </p>
            </li>
          )}
          renderInput={(params) => {
            return <TextField sx={{ maxWidth: "306px" }} {...params} required label="Round" />;
          }}
        />
        <Autocomplete
          sx={{ width: "306px" }}
          disabled={!values?.round?.name}
          clearOnBlur={true}
          value={values?.investor}
          options={investorsOptions || []}
          onChange={(e, value) => {
            setFieldValue("investor", value);
          }}
          PaperComponent={({ children }) => {
            return (
              <Paper
                sx={{
                  position: "absolute",
                  maxWidth: "306px",
                  backgroundColor: "white",
                  zIndex: "10",
                  borderRadius: "10px",
                  "& .MuiAutocomplete-option:hover": {
                    backgroundColor: "#edf1f9"
                  }
                }}
              >
                <>
                  {children}
                  <Button
                    onMouseDown={() => {
                      setCreateDealModal("investor");
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      height: "50px",
                      backgroundColor: "#fff",
                      boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                      borderRadius: "0 0 10px 10px"
                    }}
                    fullWidth
                  >
                    Add Investor
                  </Button>
                </>
              </Paper>
            );
          }}
          getOptionLabel={(value) => {
            return value.name;
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IInvestorsOptions) => (
            <li style={{ height: "43px" }} {...props} key={option.id + option.name}>
              <p
                style={{
                  width: "293px",
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  color: "#0a3142"
                }}
              >
                {option.name}
              </p>
            </li>
          )}
          renderInput={(params) => {
            return <TextField sx={{ maxWidth: "306px" }} {...params} required label="Investor" />;
          }}
        />
      </Box>
      <Box sx={{ width: "20%", display: "flex", justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          sx={{ fontSize: "16px", textTransform: "none", backgroundColor: "white", marginRight: "15px" }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={!isValid}
          sx={{ fontSize: "16px", textTransform: "none" }}
          variant="contained"
          onClick={() => handleSubmit()}
        >
          Add
        </Button>
      </Box>
      <Modal open={!!createDealModal}>
        <CreateModal
          variant={createDealModal}
          onClose={() => setCreateDealModal(null)}
          setFieldValue={setFieldValue}
          dealsOptionsState={{ setDealsOptions, dealsOptions }}
          roundsOptionsState={{ setRoundsOptions, roundsOptions }}
          investorsOptionsState={{ setInvestorsOptions, investorsOptions }}
          onDealChangeHandler={onDealChangeHandler}
          values={values}
        />
      </Modal>
    </Box>
  );
};
